<template>
  <div>
    <div class="form-group">
      <Typeahead
        v-model="counterParty"
        label="Who is the Counterparty? (Business name)"
        :items="allAvailableCounterParties"
        :errors="$page.props.errors.counter_party_id"
        placeholder="Start typing to search..."
        :tolerance="0"
        return-field="id"
        id-field="id"
        search-field="name"
        display-field="name"
        @selected="counterPartySelected"
        @noResults="noCounterparty"
      >
        <template v-slot:item="slotProps">
          <span v-if="slotProps.match.name">
            {{ slotProps.match.name }}
            <br />
          </span>
          <strong v-if="slotProps.match.address"
            ><small>Address: {{ slotProps.match.address }}</small></strong
          >
          <strong v-if="slotProps.match.phone"
            ><small>Phone: {{ slotProps.match.phone }}</small></strong
          >
        </template>
        <template v-slot:noResults>
          <h6 class="mt-2">Can’t find your Counterparty?</h6>
          <small>
            <p class="mb-2">Are you sure there is an Master Services Agreement (MSA) for this Counterparty on Clausify? If not, please <span class="a">Create an MSA first</span>. Once the MSA is executed, you can configure a Work Category for that Counterparty. </p>
          </small>
        </template>
      </Typeahead>
      <div class="row">
        <small class="form-text form-helper col-md-9 mt-2">
          Can’t find the Counterparty you’re looking for? <InertiaLink :href="route('asset.create', 'master-service-agreement')">Click here</InertiaLink> to set up a Master Services Agreement (MSA) for the Counterparty. This is mandatory to set up any Contracts for a Counterparty.
        </small>
      </div>
    </div>

    <div v-if="form.counter_party_id">
      <div class="form-group">
        <Choice
          v-model="form.role"
          @selected="msaSelected"
          :errors="$page.props.errors.role"
          label="What is your organisation's role?"
          :items="msaChoices"
        />
      </div>
    </div>

    <div v-if="form.counter_party_id && form.role && configuredWorkCategories">
      <div class="form-group">
        <label>Select a Work Category to configure</label>

        <div class="row">
          <small class="form-text form-helper col-md-9 mt-2">
            <p>This Statement of Work (SOW) relates to Work under the following Work Category (select just one).</p>
            <p>The below list highlights Work Categories you have already configured. To configure a new Work Category, <InertiaLink :href="route('asset.create', 'statement-of-work')">click here.</InertiaLink></p>
          </small>
        </div>
        <div v-for="(group, index) in groupedWorkCategories" :key="index">
          <label>{{ index }}</label>
          <div class="row">
            <div class="col-6" v-for="workCategory in group" :key="workCategory.id">
              <Radio
                :label="workCategory.label"
                v-model="form.work_category_type_id"
                @input="$page.props.errors.work_category_type_id = null"
                :val="workCategory.id"
                :disabled="!configuredWorkCategories.includes(workCategory.id)"
              />
            </div>
          </div>
          <hr
            v-if="Object.keys(groupedWorkCategories)[Object.keys(groupedWorkCategories).length - 1] !== index"
            class="my-4"
          />
        </div>
        <Error
          v-if="$page.props.errors.work_category_type_id && $page.props.errors.work_category_type_id.length"
          :errors="$page.props.errors.work_category_type_id"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.standalone"
          @selected="$page.props.errors.standalone = []"
          :errors="$page.props.errors.standalone"
          label="Is this a standalone contract?"
          :items="standaloneItems"
          :grow="false"
          @input="form.programme_of_work_id = null"
        />
        <div v-if="form.standalone === false" class="card mb-5">
          <div class="card-body">
            <div class="form-group mb-5">
              <Typeahead
                v-model="programmeOfWork"
                :errors="$page.props.errors.programme_of_work_id"
                :items="programmeOfWorksItems"
                label="Does this Contract belong to an existing Programme of Work?"
                :disabled="programmeOfWorksItems.length === 0"
                placeholder="Select an existing Programme of Work"

                :tolerance="0"
                return-field="id"
                id-field="id"
                search-field="name"
                display-field="name"
                @selected="programmeOfWorkSelected"
              />
            </div>

            <div class="d-flex">
              <div class="flex-grow-1"><hr /></div>
              <span class="flex-grow-0 flex-shrink-0 my-1 px-2">OR</span>
              <div class="flex-grow-1"><hr /></div>
            </div>

            <div class="mt-5" :class="{ 'opacity-25': form.programme_of_work_id }">
              <h5>Create a new Programme of Work for this Contract</h5>
              <p class="mb-5">Set up a new Programme of Work to which you can link this Contract to other Contracts.</p>

              <div class="form-group">
                <TextInput
                  class="text-primary"
                  v-model="pow.name"
                  :errors="errors.name"
                  type="text"
                  label="What would you like to call this Programme of Work?"
                  autocapitalize="off"
                  @input="errors.name = []"
                  validateSuccess
                  validateError
                />
              </div>

              <div class="form-group">
                <TextArea
                  class="mb-4 text-primary"
                  label="Briefly describe the purpose of this Programme of Work"
                  v-model="pow.description"
                  :errors="errors.description"
                  autocapitalize="off"
                  rows="4"
                  @input="errors.description = []"
                  validateSuccess
                  validateError
                />
              </div>

              <button @click.prevent="save" class="btn btn-primary mr-2 float-left">Create</button>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <Choice
          v-model="form.personal_data"
          @selected="$page.props.errors.personal_data = []"
          :errors="$page.props.errors.personal_data"
          label="Will the Supplier be required to Process Personal Data under this Contract?"
          :items="personalDataItems"
          :grow="false"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.acceptance"
          @selected="$page.props.errors.acceptance = []"
          :errors="$page.props.errors.acceptance"
          label="Will Acceptance apply to any of the Products delivered under this Contract?"
          :items="acceptanceItems"
          :grow="false"
        />
      </div>

      <div class="form-group">
        <Choice
          v-model="form.ipr"
          @selected="$page.props.errors.ipr = []"
          :errors="$page.props.errors.ipr"
          label="How will Contract IPR be categorised under this Contract?"
          :items="irpItems"
        />
      </div>
    </div>
  </div>
</template>

<script>
import mixin from './mixin';

export default {
  mixins: [mixin],

  props: {
    workCategories: Array,
    programmeOfWorks: Array,
  },

  data() {
    return {
      counterPartiesData: this.counterParties,
      counterParty: null,
      programmeOfWork: null,
      form: {
        counter_party_id: this.asset ? this.asset.counter_party_id : null,
        counter_party_type: this.asset ? this.asset.counter_party_type : null,
        role: this.asset ? this.asset.role : null,
        asset_id: this.asset ? this.asset.asset_id : null,
        work_category_type_id: this.asset ? this.asset.work_category_type_id : null,
        programme_of_work_id: this.asset ? this.asset.programme_of_work_id : null,

        standalone: this.asset ? this.asset.fields.standalone : null,
        personal_data: this.asset ? this.asset.fields.personal_data : null,
        acceptance: this.asset ? this.asset.fields.acceptance : null,
        ipr: this.asset ? this.asset.fields.ipr : null,
      },
      configuredWorkCategories: [],
      pow: {
        name: '',
        description: '',
      },
      programmeOfWorksItems: [],
      showCreate: true,
      adding: false,
      sending: false,
      fetchingConfiguredWorkCategories: true,
      standaloneItems: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      personalDataItems: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      acceptanceItems: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      irpItems: [
        {
          label: 'Type A',
          value: 'A',
          helper:
            'Owned by Supplier with licence to Customer (see Clause 17.2.2. of the MSA)',
        },
        {
          label: 'Type B',
          value: 'B',
          helper:
            'Owned by Customer, with broad licence back to Supplier (see Clause 17.2.3 of the MSA)',
        },
        {
          label: 'Type C',
          value: 'C',
          helper:
            'Owned by Customer, with very narrow licence back to Supplier (see Clause 17.2.4 of the MSA)',
        },
      ],
      errors: {},
      supplierMsa: null,
      customerMsa: null,
    };
  },

  computed: {
    allAvailableCounterParties() {
      const modifiedOrganisations = this.organisations.map((org) => ({
        id: org.id,
        type: 'App\\Models\\Organisation',
        name: org.name,
      }));

      const modifiedCounterParties = this.counterPartiesData.map((cp) => ({
        id: cp.id,
        type: 'App\\Models\\CounterParty',
        name: cp.name,
        address: cp.address,
        phone: cp.phone,
      }));

      return [...modifiedOrganisations, ...modifiedCounterParties].filter(
        (org) => org.id,
      );
    },

    groupedWorkCategories() {
      const groupBy = (key) => (array) => array.reduce((objectsByKeyValue, obj) => {
        const value = obj[key];
        // eslint-disable-next-line no-param-reassign
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(
          obj,
        );
        return objectsByKeyValue;
      }, {});
      const groupByCategory = groupBy('group');
      return groupByCategory(this.workCategories);
    },

    correctRoute() {
      return this.form.counter_party_type === 'App\\Models\\CounterParty' ? 'counter-party' : 'organisation';
    },

    msaChoices() {
      return [
        {
          label: 'Selling goods and/or services (Supplier)',
          value: this.supplierMsa ? 'supplier' : '',
        }, {
          label: 'Buying goods and/or services (Customer)',
          value: this.customerMsa ? 'customer' : '',
        },
      ];
    },
  },

  methods: {
    counterPartySelected(cp) {
      this.$page.props.errors.counter_party_id = null;

      if (cp) {
        this.form.counter_party_id = cp.id;
        this.form.counter_party_type = cp.type;

        this.fetchExecutedMasterServiceAgreements();
        this.fetchConfiguredProgrammesOfWork();
      } else {
        this.form.counter_party_id = null;
        this.form.counter_party_type = null;
        this.form.role = null;
        this.form.asset_id = null;
        this.supplierMsa = null;
        this.customerMsa = null;
      }
    },

    programmeOfWorkSelected(pow) {
      this.$page.props.errors.programme_of_work_id = null;

      if (pow) {
        this.form.programme_of_work_id = pow.id;
      } else {
        this.form.programme_of_work_id = null;
      }
    },

    msaSelected() {
      this.form.asset_id = this.form.role === 'supplier' ? this.supplierMsa.id : this.customerMsa.id;

      this.configuredWorkCategories = [];
      this.fetchConfiguredWorkCategories();
      this.$page.props.errors.asset_id = [];
    },

    fetchExecutedMasterServiceAgreements() {
      window.axios
        .get(this.route('counter-party.asset.index', [this.form.counter_party_id, 'master-service-agreement']))
        .then((response) => {
          this.supplierMsa = response.data.length ? response.data.find((msa) => msa.role === 'supplier') : null;
          this.customerMsa = response.data.length ? response.data.find((msa) => msa.role === 'customer') : null;
        })
        .catch((error) => {
          throw new Error(error);
        });
    },

    fetchConfiguredWorkCategories() {
      window.axios
        .get(this.route(`${this.correctRoute}.configured-work-categories`, [
          this.form.counter_party_id,
          this.form.asset_id,
        ]))
        .then((response) => {
          this.configuredWorkCategories = response.data;
          this.sending = false;
        });
    },

    fetchConfiguredProgrammesOfWork() {
      window.axios
        .get(
          this.route(
            `${this.correctRoute}.configured-programmes-of-work`,
            this.form.counter_party_id,
          ),
        )
        .then(({ data }) => {
          this.programmeOfWorksItems = data || [];
        });
    },

    create() {
      this.showCreate = true;
    },

    cancel() {
      this.showCreate = false;
      this.pow.name = '';
      this.pow.description = '';
    },

    save() {
      this.adding = true;

      const post = this.pow;
      post.ownerable_id = this.form.counter_party_id;
      post.ownerable_type = this.form.counter_party_type;

      this.$nextTick(() => {
        window.axios
          .post('/programme-of-work', post)
          .then((response) => {
            this.adding = false;
            this.cancel();
            this.programmeOfWorksItems.push(response.data);
            this.form.programme_of_work_id = response.data.id;
            this.programmeOfWork = response.data;
          })
          .catch((error) => {
            this.sending = false;
            this.errors = error.response.data.errors;
            throw new Error(error);
          });
      });
    },

    noCounterparty() {
      this.$inertia.visit(this.route('asset.create', 'master-service-agreement'));
    },
  },
};
</script>
